import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useCallback, useEffect, useMemo, useState } from "react";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import { yupResolver } from "@hookform/resolvers/yup";
import { createRide, deleteRide, getRideDetails, updateRide } from "api/ride";
import CustomReactSelect from "components/CustomReactSelect";
import HeaderCard from "components/DataTable/HeaderCard";
import ImageUploadField from "components/ImageUploadField";
import Loading from "components/Loading";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import StaffSelector from "components/StaffSelector";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useGlobalStore from "store/useGlobalStore";
import Swal from "sweetalert2";
import { getStatusFilters, jsonToFormData, removeEmptyFields } from "utils/helper";
import * as yup from "yup";
import { parentRoute } from ".";

const rideSchema = yup.object({
    rd_name: yup.string().trim().required("Ride name is required").max(100, "Max 100 characters"),
    rd_description: yup.string().max(200, "Max 200 characters").nullable(),
    rd_staff: yup.array().of(yup.number()).nullable(),
});

const AddRide = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setRides, setPrevSelectedRow } = useGlobalStore();
    const [formData, setFormData] = useState({
        rd_name: "",
        rd_description: "",
        rd_status: "",
        rd_image: null,
    });
    const [formErrors, setFormErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const statusSelection = getStatusFilters(false)
    const id = Number(location.pathname.split('/')[2]);

    const isAdd = useMemo(() =>
        location.pathname.includes(`${parentRoute}/add-ride`),
        [location.pathname]
    );

    useEffect(() => {
        const fetchData = async () => {
            if (isAdd) return;

            if (!id || isNaN(id)) {
                navigate(parentRoute, { state: location.state });
                return;
            }
            const itemId = id;

            setIsLoading(true);
            try {
                const { data: details } = await getRideDetails(itemId);
                if (details) {
                    setFormData({
                        id: details.id,
                        rd_name: details.name,
                        rd_description: details.description,
                        rd_status: details.status?.id,
                        rd_image: details.img_url || null,
                        rd_staff: details.users.map(user => user.id)
                    });
                }
            } catch (error) {
                toast.error("Failed to find ride data");
                navigate(parentRoute, { state: location.state });
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);


    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes'
        });

        if (result.isConfirmed) {
            await deleteRide(formData.id)
            toast.success("Ride deleted successfully");
            navigate(parentRoute);
        }
    }

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setFormErrors({});

        try {
            let passData = jsonToFormData(removeEmptyFields(formData))

            if (isAdd) {
                await createRide(passData);
                toast.success("Ride added successfully");
                navigate(parentRoute, { state: location.state });
            } else {
                await updateRide(formData.id, passData);
                toast.success("Ride updated successfully");
            }
        } catch (err) {
            setFormErrors(err?.data?.errors || {});
            console.log(err)
            toast.error(err?.data?.message || (isAdd ? "Failed to create ride" : "Failed to update ride"));
        } finally {
            setIsLoading(false);
            setRides([])
        }
    }, [formData, navigate, isAdd]);

    const {
        trigger,
        setValue,
        formState: { errors: rhfErrors },
    } = useForm({
        resolver: yupResolver(rideSchema),
        mode: "onSubmit",
        defaultValues: {
            rd_name: "",
            rd_description: "",
            rd_status: "",
            rd_image: null,
            rd_staff: [],
        },
    });

    // Sync your formData to RHF for validation
    useEffect(() => {
        Object.entries(formData).forEach(([k, v]) => setValue(k, v, { shouldValidate: false, shouldDirty: false }));
    }, [formData, setValue]);

    // Map RHF errors to your formErrors for UI
    useEffect(() => {
        const mapped = {};
        Object.entries(rhfErrors || {}).forEach(([k, v]) => {
            mapped[k] = v?.message;
        });
        setFormErrors(mapped);
    }, [rhfErrors]);

    const handleValidatedSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setFormErrors({});
            const valid = await trigger();
            if (!valid) {
                toast.error("Please fix the validation errors before submitting.");
                return;
            }
            await handleSubmit(e); // your original submit logic
        },
        [trigger, handleSubmit]
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={4} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <HeaderCard
                                title={isAdd ? "Create a New Ride" : "Edit Ride"}
                                showBackButton={true}
                                onBackClick={() => { navigate(parentRoute, { state: location.state }); setPrevSelectedRow(id, parentRoute) }}
                            />
                            {isLoading && !isAdd ? <Loading /> :
                                <MDBox pt={3} px={2} pb={2}>
                                    <form noValidate onSubmit={handleValidatedSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 'auto' }}>
                                        <Grid item xs={12}>
                                            <Grid container spacing={3} style={{ flexGrow: 1 }}>
                                                <Grid item xs={12} md={isAdd ? 12 : 6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Ride name"
                                                        name="rd_name"
                                                        value={formData?.rd_name || ''}
                                                        helperText={formErrors.rd_name}
                                                        error={Boolean(formErrors.rd_name)}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </Grid>
                                                {!isAdd && <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        label="Status"
                                                        name="rd_status"
                                                        variant="outlined"
                                                        value={statusSelection.find(status => status.value === formData?.rd_status ?? formData?.rd_status?.id)}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'rd_status', value: selectedOption?.value } })}
                                                        options={statusSelection}
                                                        required
                                                    />
                                                </Grid>}
                                                <Grid item xs={12}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Description"
                                                        name="rd_description"
                                                        helperText={formErrors.rd_description}
                                                        error={Boolean(formErrors.rd_description)}
                                                        value={formData?.rd_description || ''}
                                                        onChange={handleChange}
                                                        multiline
                                                        minRows={2}
                                                        inputProps={{ maxLength: 200 }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <StaffSelector
                                                        label="Responsible Staff"
                                                        placeholder="Search staff..."
                                                        isMulti
                                                        value={formData?.rd_staff || []}
                                                        onChange={options =>
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                rd_staff: options || []
                                                            }))
                                                        }
                                                        required={false}
                                                    />
                                                </Grid>


                                                <Grid item xs={12}>
                                                    <ImageUploadField
                                                        value={formData?.rd_image}
                                                        onChange={(file) => setFormData(prev => ({ ...prev, rd_image: file }))}
                                                        name="rd_image"
                                                        label="Ride Image"
                                                    />
                                                </Grid>
                                                {/* <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        label="Checklist Items"
                                                        name="rd_checklist_item"
                                                        variant="outlined"
                                                        isMulti
                                                        value={
                                                            formData?.rd_checklist_item
                                                                ? checklistOptions.filter(opt =>
                                                                    formData.rd_checklist_item.includes(opt.value)
                                                                )
                                                                : []
                                                        }
                                                        onChange={selectedOptions =>
                                                            handleChange({
                                                                target: {
                                                                    name: 'rd_checklist_item',
                                                                    value: selectedOptions ? selectedOptions.map(opt => opt.value) : []
                                                                }
                                                            })
                                                        }
                                                        options={checklistOptions}
                                                        placeholder="Select checklist items"
                                                    />
                                                </Grid> */}

                                                <Grid item xs={12}>
                                                    <Grid container>
                                                        <Grid item>
                                                            {!isAdd && <MDButton
                                                                variant="contained"
                                                                color="error"
                                                                onClick={handleDelete}
                                                                loading={isLoading}
                                                            >
                                                                Delete
                                                            </MDButton>}
                                                        </Grid>
                                                        <Grid item xs />
                                                        <Grid item>
                                                            <MDBox display="flex" justifyContent="flex-end">
                                                                <MDButton
                                                                    variant="contained"
                                                                    color="primary"
                                                                    type="submit"
                                                                    loading={isLoading}
                                                                >
                                                                    Submit
                                                                </MDButton>
                                                            </MDBox>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </MDBox>}
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    );
};

export default AddRide;
